import React, { useEffect, useRef } from 'react';
import styles from './AnimatedBackground.module.css';


const NODES = [
  { x: 8,  y: 12 }, { x: 22, y: 38 }, { x: 15, y: 68 },
  { x: 38, y: 18 }, { x: 48, y: 55 }, { x: 35, y: 82 },
  { x: 62, y: 10 }, { x: 72, y: 42 }, { x: 58, y: 75 },
  { x: 85, y: 22 }, { x: 92, y: 58 }, { x: 78, y: 88 },
];


const EDGES = (() => {
  const edges = [];
  const THRESHOLD = 30;
  for (let i = 0; i < NODES.length; i++) {
    for (let j = i + 1; j < NODES.length; j++) {
      const dx = NODES[i].x - NODES[j].x;
      const dy = NODES[i].y - NODES[j].y;
      if (Math.sqrt(dx * dx + dy * dy) < THRESHOLD) {
        edges.push([i, j]);
      }
    }
  }
  return edges;
})();

export default function AnimatedBackground() {
  return (
    <div className={styles.bg} aria-hidden="true">
      <svg
        className={styles.svg}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <defs>
      
          <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FF3621" stopOpacity="0.65" />
            <stop offset="100%" stopColor="#FF3621" stopOpacity="0" />
          </radialGradient>

          <linearGradient id="edgeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="#FF3621" stopOpacity="0" />
            <stop offset="50%"  stopColor="#FF3621" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#FF3621" stopOpacity="0" />
          </linearGradient>
        </defs>

 
        {EDGES.map(([a, b], i) => (
          <line
            key={`e-${i}`}
            x1={NODES[a].x} y1={NODES[a].y}
            x2={NODES[b].x} y2={NODES[b].y}
            stroke="url(#edgeGrad)"
            strokeWidth="0.12"
            className={styles.edge}
            style={{ animationDelay: `${(i * 0.37) % 2}s` }}
          />
        ))}

  
        {NODES.map((n, i) => (
          <g key={`n-${i}`}>
       
            <circle
              cx={n.x} cy={n.y} r="1.4"
              fill="url(#nodeGlow)"
              className={styles.halo}
              style={{ animationDelay: `${(i * 0.45) % 5}s` }}
            />

            <circle
              cx={n.x} cy={n.y} r="0.28"
              fill="#FF3621"
              fillOpacity="0.22"
              className={styles.dot}
              style={{ animationDelay: `${(i * 0.45) % 5}s` }}
            />
          </g>
        ))}

        
        <circle
          cx="88" cy="8" r="7"
          fill="none"
          stroke="#FF3621"
          strokeWidth="0.12"
          strokeOpacity="0.1"
          className={styles.floatSlow}
        />
        <circle
          cx="88" cy="8" r="4.5"
          fill="none"
          stroke="#FF3621"
          strokeWidth="0.08"
          strokeOpacity="0.08"
          className={styles.floatSlow}
          style={{ animationDelay: '1.2s' }}
        />

        
        <circle
          cx="10" cy="90" r="6"
          fill="none"
          stroke="#0d0d0d"
          strokeWidth="0.1"
          strokeOpacity="0.05"
          className={styles.floatMed}
        />

       
        <g className={styles.floatMed} style={{ animationDelay: '2s' }}>
          <line x1="50" y1="46" x2="50" y2="54" stroke="#FF3621" strokeWidth="0.1" strokeOpacity="0.12" />
          <line x1="46" y1="50" x2="54" y2="50" stroke="#FF3621" strokeWidth="0.1" strokeOpacity="0.12" />
          <circle cx="50" cy="50" r="3" fill="none" stroke="#FF3621" strokeWidth="0.1" strokeOpacity="0.08" />
        </g>

        
        <polygon
          points="18,4 20,8 18,12 16,8"
          fill="none"
          stroke="#FF3621"
          strokeWidth="0.12"
          strokeOpacity="0.1"
          className={styles.floatFast}
        />

       
        <polygon
          points="82,88 84,92 82,96 80,92"
          fill="none"
          stroke="#0d0d0d"
          strokeWidth="0.1"
          strokeOpacity="0.06"
          className={styles.floatFast}
          style={{ animationDelay: '1.5s' }}
        />

        
        <line
          x1="0" y1="0" x2="100" y2="100"
          stroke="#FF3621"
          strokeWidth="0.06"
          strokeOpacity="0.04"
        />
        <line
          x1="100" y1="0" x2="0" y2="100"
          stroke="#FF3621"
          strokeWidth="0.06"
          strokeOpacity="0.03"
        />
      </svg>

      
      <ParticleCanvas />
    </div>
  );
}


function ParticleCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    /* Create particles */
    const COUNT = 28;
    const particles = Array.from({ length: COUNT }, () => ({
      x:   Math.random() * window.innerWidth,
      y:   Math.random() * window.innerHeight,
      r:   Math.random() * 1.6 + 0.4,
      vx:  (Math.random() - 0.5) * 0.22,
      vy:  (Math.random() - 0.5) * 0.22,
      a:   Math.random() * 0.12 + 0.03,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(p => {
        /* Move */
        p.x += p.vx;
        p.y += p.vy;

        /* Wrap around */
        if (p.x < -4) p.x = canvas.width  + 4;
        if (p.x > canvas.width  + 4) p.x = -4;
        if (p.y < -4) p.y = canvas.height + 4;
        if (p.y > canvas.height + 4) p.y = -4;

        /* Draw */
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(230, 51, 41, ${p.a})`;
        ctx.fill();
      });

      raf = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={ref} className={styles.canvas} />;
}